<template>
  <section>
    <div :class="{ 'order-detail-margin-bottom': !inDialog }" class="order-detail">
      <b-loading :active="loading" />
      <h2 v-if="!inDialog" class="order-detail__title ml-4">
        例行性訂購排程設定
      </h2>
      <div class="flex gap-3 items-center mt-6 pl-4">
        <span class="font-medium text-xl">選擇訂購部門</span>
        <w-button
          type="icon"
          @click="departmentCollapsed = !departmentCollapsed"
        >
          <i
            :class="{ collapsed: departmentCollapsed }"
            class="icon-drop-down text-2xl text-gray-500"
          />
        </w-button>
      </div>
      <div v-if="!departmentCollapsed" class="columns order-detail__content">
        <div class="column">
          <div class="detail-card detail-card--info mb-0">
            <div class="flex items-center">
              <h3 class="detail-card__title">
                訂購部門名單
              </h3>
              <div
                v-if="!orderInfo?.departmentIds?.length && validData"
                class="flex items-center mb-4"
              >
                <span class="icon-alert-circle ml-3.5 text-red-500 text-lg" />
                <span class="ml-2.5 text-red-500 text-sm">訂購部門為必選</span>
              </div>
            </div>
            <b-checkbox
              v-model="isAllDepartmentSelected"
              :disabled="viewMode"
              class="mr-3"
              native-value="all"
              @input="selectAllDepartments"
            >
              全部
            </b-checkbox>
            <b-checkbox
              v-for="info in departments"
              v-model="orderInfo.departmentIds"
              class="mr-3"
              :disabled="viewMode"
              :native-value="info.id"
              :key="info.id"
              @input="departmentChanged"
            >
              {{ info.name }}
            </b-checkbox>
          </div>
        </div>
      </div>
      <div class="flex gap-3 items-center mt-6 pl-4">
        <span class="font-medium text-xl">選擇訂購店家</span>
        <w-button
          v-if="!viewMode"
          size="sm"
          @click.prevent="openStoreSelectorModal = true"
        >
          選擇店家
        </w-button>
        <w-button
          type="icon"
          @click="storeCollapsed = !storeCollapsed"
        >
          <i
            :class="{ collapsed: storeCollapsed }"
            class="icon-drop-down text-2xl text-gray-500"
          />
        </w-button>
      </div>
      <div v-if="!storeCollapsed" class="columns order-detail__content">
        <div class="column">
          <div class="detail-card detail-card--info mb-0">
            <div class="flex items-center">
              <h3 class="detail-card__title">
                店家資訊
              </h3>
              <div
                v-if="!menu && validData"
                class="flex items-center mb-4"
              >
                <span class="icon-alert-circle ml-3.5 text-red-500 text-lg" />
                <span class="ml-2.5 text-red-500 text-sm">訂購店家為必選，請點擊上方「選擇店家」按鈕，選擇訂購店家</span>
              </div>
            </div>
            <div v-if="menu" class="columns is-flex-wrap-wrap">
              <div class="column is-half">
                <b-field
                  horizontal
                  label="店家名稱">
                  {{ menu?.storeInfo.store_detail.name }}
                </b-field>
              </div>
              <div v-if="storePhone" class="column is-half">
                <b-field horizontal label="電話訂購">
                  {{ storePhone }}
                </b-field>
              </div>
              <div v-if="storeClosedDays" class="column is-half">
                <b-field horizontal label="公休日">
                  {{ storeClosedDays }}
                </b-field>
              </div>
              <div v-if="storeOrderMethods.length" class="column is-half">
                <b-field horizontal class="actionable">
                  <template #label>
                    <label>線上訂購</label>
                  </template>
                  <div class="flex gap-x-2 items-center">
                    <div class="flex gap-x-4 items-center">
                      <img
                        v-for="method in storeOrderMethods"
                        :key="`image-${method.key}`"
                        :alt="`${method.title}圖示`"
                        :src="getOrderMethodImage(method.icon)"
                      >
                    </div>
                    <b-tooltip
                      :auto-close="['outside', 'escape']"
                      :triggers="['click']"
                      class="cursor-pointer"
                      multilined
                      position="is-bottom"
                      size="is-small"
                      type="is-light"
                    >
                      <i class="icon-drop-down ml-1 text-gray-500" />
                      <template v-slot:content>
                        <div
                          v-for="method in storeOrderMethods"
                          :key="`method-${method.key}`"
                          class="tooltip-content__order-method"
                        >
                          <img
                            :alt="`${method.title}圖示`"
                            :key="`image-${method.key}`"
                            :src="getOrderMethodImage(method.icon)"
                            class="min-w-[24px]"
                          >
                          <a
                            v-if="method.linkTitle"
                            :href="menu?.storeInfo.store_order_way[method.key]"
                            target="_blank"
                          >
                            {{ method.linkTitle }}
                          </a>
                          <p
                            v-else
                            :title="menu?.storeInfo.store_order_way[method.key]"
                          >
                            {{ menu?.storeInfo.store_order_way[method.key] }}
                          </p>
                        </div>
                      </template>
                    </b-tooltip>
                  </div>
                </b-field>
              </div>
              <div v-if="storePaymentMethods" class="column is-half">
                <b-field horizontal label="收款方式">
                  {{ storePaymentMethods }}
                </b-field>
              </div>
            </div>
            <span v-else class="text-gray-500">尚未選擇店家</span>
          </div>
        </div>
      </div>
      <div class="flex gap-3 items-center mt-6 pl-4">
        <span class="font-medium text-xl">設定訂購資訊</span>
        <w-button
          type="icon"
          @click="settingCollapsed = !settingCollapsed"
        >
          <i
            :class="{ collapsed: settingCollapsed }"
            class="icon-drop-down text-2xl text-gray-500"
          />
        </w-button>
      </div>
      <div v-if="!settingCollapsed" class="columns order-detail__content">
        <div class="column">
          <div class="detail-card detail-card--info subsidy-info">
            <div
              class="
                flex
                flex-wrap
                items-baseline
                justify-start
                mb-4
            "
            >
              <h3 class="font-medium mr-4">
                補助額度
              </h3>
              <span class="text-gray-500 text-sm">
                系統將依據補助額度計算人員自付額
              </span>
            </div>
            <div>
              <b-field
                horizontal
                :type="{
                  'is-danger': v$.orderInfo.subsidy.$invalid
                }"
                :message="v$.orderInfo.subsidy?.$silentErrors[0]?.$message">
                <template #label>
                  <span class="required">每人補助金額</span>
                </template>
                <b-input
                  v-model.number="orderInfo.subsidy"
                  :disabled="viewMode"
                  class="w-32"
                  placeholder="$0"
                  @keypress.native="filterNonPositiveNumber"
                />
              </b-field>
            </div>
          </div>
          <div class="detail-card detail-card--info">
            <div
              class="
                flex
                flex-wrap
                items-baseline
                justify-start
                mb-4
            "
            >
              <h3 class="font-medium mr-4">
                消費額度
              </h3>
              <span class="text-gray-500 text-sm">
                設定消費額度後，僅能下單額度內的訂購。
              </span>
            </div>
            <div>
              <b-field v-if="showLimitOption(consumptionLimitList.NO_LIMIT)">
                <b-radio
                  :disabled="viewMode"
                  :native-value="consumptionLimitList.NO_LIMIT"
                  v-model="orderInfo.limit"
                >
                  <span>不限制</span>
                </b-radio>
              </b-field>
              <div
                v-if="showLimitOption(consumptionLimitList.AMOUNT)"
                class="flex items-center mb-3"
              >
                <b-field class="mb-0 mr-2">
                  <b-radio
                    :disabled="viewMode"
                    :native-value="consumptionLimitList.AMOUNT"
                    v-model="orderInfo.limit"
                  >
                    <span>每人限制總消費金額</span>
                  </b-radio>
                </b-field>
                <b-field
                  :type="{
                    'is-danger': v$.limitAmount.$invalid
                  }"
                  :message="v$.limitAmount?.$silentErrors[0]?.$message ? '每人限制總消費金額須大於0' : ''"
                >
                  <b-input
                    v-model.number="limitAmount"
                    :disabled="orderInfo.limit !== consumptionLimitList.AMOUNT || viewMode"
                    class="w-32"
                    placeholder="填入金額"
                    @keypress.native="filterNonPositiveNumber"
                  />
                </b-field>
              </div>
              <div
                v-if="showLimitOption(consumptionLimitList.ITEMS)"
                class="flex items-center mb-3"
              >
                <b-field class="mb-0 mr-2">
                  <b-radio
                    :disabled="viewMode"
                    :native-value="consumptionLimitList.ITEMS"
                    v-model="orderInfo.limit"
                  >
                    <span>每人限制總訂購項目數</span>
                  </b-radio>
                </b-field>
                <b-field
                  :type="{
                    'is-danger': v$.limitItems.$invalid
                  }"
                  :message="v$.limitItems?.$silentErrors[0]?.$message ? '每人限制總訂購項目數須大於0' : ''"
                >
                  <b-input
                    v-model.number="limitItems"
                    :disabled="orderInfo.limit !== consumptionLimitList.ITEMS || viewMode"
                    class="w-32"
                    placeholder="填入項目數"
                    @keypress.native="filterNonPositiveNumber"
                  />
                </b-field>
              </div>
            </div>
          </div>
          <div class="detail-card detail-card--info">
            <div
              class="
                flex
                flex-wrap
                items-baseline
                justify-start
                mb-4
            "
            >
              <h3 class="font-medium mr-4">
                品項備註設定
              </h3>
              <span class="text-gray-500 text-sm">
                開放使用者可於訂單內輸入品項備註。
              </span>
            </div>
            <b-field horizontal label="開放品項備註">
              <b-switch
                v-model="orderInfo.enableOrderNote"
                :disabled="viewMode"
                class="mx-2"
                size="is-small"
              />
            </b-field>
          </div>
          <div class="detail-card detail-card--info">
            <div
              class="
                flex
                flex-wrap
                items-baseline
                justify-start
                mb-4
            "
            >
              <h3 class="detail-card__title mr-4">設定品項庫存量</h3>
              <span class="text-gray-500 text-sm">
                可針對不同品項設定庫存量以限制訂購總數，留空表示不限制。
              </span>
            </div>
            <template v-if="menu">
              <label class="font-medium text-xl">
                {{ menu?.name }}
              </label>
              <b-table :data="menuItems" class="mt-4">
                <b-table-column
                  header-class="text-xs"
                  field="index"
                  label="No."
                  v-slot="props"
                  width="28"
                >
                  {{ props.index + 1 }}.
                </b-table-column>
                <b-table-column
                  field="name"
                  header-class="text-xs"
                  cell-class="min-w-[150px]"
                  label="品項名稱"
                  v-slot="props"
                >
                  <b-tooltip
                    :label="props.row.name"
                    position="is-bottom"
                    type="is-dark"
                  >
                    {{ props.row.name }}
                  </b-tooltip>
                </b-table-column>
                <b-table-column
                  field="category"
                  header-class="min-w-[84px] text-xs"
                  label="品項類別"
                  v-slot="props"
                >
                  {{ props.row.category }}
                </b-table-column>
                <b-table-column
                  header-class="text-xs"
                  cell-class="max-w-[48px]"
                  field="price"
                  label="單價"
                  numeric
                  v-slot="props"
                  width="48"
                >
                  {{ props.row.price | priceFormat }}
                </b-table-column>
                <b-table-column
                  header-class="text-xs"
                  cell-class="max-w-[68px]"
                  field="limit"
                  label="庫存量"
                  v-slot="props"
                  width="68"
                >
                  <b-field
                    :type="
                      v$.menuItems.$each.$response.$errors[props.index].limit?.length
                      ? 'is-danger' : ''
                    "
                    :message="v$.menuItems.$each.$response
                      .$errors[props.index].limit?.[0]?.$message"
                  >
                    <b-input
                      :disabled="viewMode"
                      :min="minEntreeLimit"
                      :max="maxEntreeLimit"
                      :ref="`limitInput${props.index}`"
                      v-model="props.row.limit"
                      class="detail-card__limit-field"
                      placeholder="-"
                      type="number"
                      @input="v$.menuItems.$touch"
                      @keydown.down.native.stop.prevent="focusOnLimitInput(props.index + 1)"
                      @keydown.enter.native.stop.prevent="focusOnLimitInput(props.index + 1)"
                      @keydown.up.native.stop.prevent="focusOnLimitInput(props.index - 1)"
                    />
                  </b-field>
                </b-table-column>

                <template #empty>
                  <div class="text-center">無菜單品項</div>
                </template>
              </b-table>
            </template>
            <span v-else class="text-gray-500">尚未選擇店家</span>
          </div>
          <div class="auto-notify detail-card">
            <div
              class="
                flex
                flex-wrap
                items-baseline
                justify-start
                mb-4
            "
            >
              <h3 class="font-medium mr-4">
                自動發送提醒通知
              </h3>
              <span class="text-gray-500 text-sm">
                開啟通知發送功能，受邀成員將收到電子郵件通知。
              </span>
            </div>
            <div>
              <b-field horizontal>
                <template #label>
                  <label class="mr-1">公告通知</label>
                  <b-tooltip
                    label="開啟本功能，系統將針對此次排程的訂購，自動發送公告通知。"
                    multilined
                    type="is-dark"
                  >
                    <i class="icon-help-circle-outline text-gray-500" />
                  </b-tooltip>
                </template>
                <div class="flex items-center">
                  <b-switch
                    v-model="orderInfo.notifyReserve"
                    :disabled="viewMode"
                    class="mx-2"
                    size="is-small"
                  />
                </div>
              </b-field>
              <b-field horizontal>
                <template #label>
                  <label class="mr-1">開放送單通知</label>
                  <b-tooltip
                    label="開啟本功能，系統將於此次訂購開始後，自動發送訂購通知。"
                    multilined
                    type="is-dark"
                  >
                    <i class="icon-help-circle-outline text-gray-500" />
                  </b-tooltip>
                </template>
                <div class="is-flex is-align-items-center">
                  <b-switch
                    v-model="orderInfo.notifyStart"
                    :disabled="viewMode"
                    class="mx-2"
                    size="is-small"
                  />
                </div>
              </b-field>
              <b-field horizontal>
                <template #label>
                  <label class="mr-1">截止收單通知</label>
                  <b-tooltip
                    label="當設定通知時間，早於截止時間扣掉目前時間，則會立刻通知。"
                    multilined
                    type="is-dark"
                  >
                    <i class="icon-help-circle-outline text-gray-500" />
                  </b-tooltip>
                </template>
                <div class="flex flex-wrap items-center" ref="flexBox">
                  <div class="flex items-center">
                    <b-switch
                      v-model="orderInfo.notifyDeadline"
                    :disabled="viewMode"
                    class="mx-2"
                      size="is-small"
                    />
                  </div>
                  <span class="text-gray-50 mx-6" :style="{ opacity : dividerHide ? 0 : 1 }">
                    |
                  </span>
                  <div class="flex items-center flex-wrap">
                    <span class="text-gray-500 mr-2">訂購截止前</span>
                    <div class="flex items-center flex-wrap">
                      <b-field class="mr-2">
                        <b-select
                          v-model="orderInfo.shiftMin"
                          :disabled="!orderInfo.notifyDeadline || viewMode"
                          placeholder="請選擇時間"
                        >
                          <option
                            v-for="option in notifyDeadlineOptions"
                            :value="option.value"
                            :key="option.label"
                          >
                            {{ option.label }}
                          </option>
                        </b-select>
                      </b-field>
                    <span class="text-gray-500">發送通知。</span>
                    </div>
                  </div>
                </div>
              </b-field>
              <b-field horizontal>
                <template #label>
                  <label class="mr-1">領取通知</label>
                  <b-tooltip
                    label="系統將於訂購人員選擇之領取時段，自動發送領取通知。"
                    multilined
                    type="is-dark"
                  >
                    <i class="icon-help-circle-outline text-gray-500" />
                  </b-tooltip>
                </template>
                <div class="is-flex is-align-items-center">
                  <b-switch
                    v-model="orderInfo.enableSendLineNotifyInReceiveTime"
                    :disabled="viewMode"
                    class="mx-2"
                    size="is-small"
                  />
                </div>
              </b-field>
              <div class="border-b border-light-gray-500 my-7" />
              <div
                class="
                  flex
                  flex-wrap
                  items-baseline
                  justify-start
                  mb-4
              "
              >
                <h3 class="font-medium mr-4">
                  自動寄送報表
                </h3>
                <p class="text-gray-500 text-sm">
                  開啟寄送的報表，會於對應的時間點自動寄出通知至指定電子郵件信箱。
                </p>
              </div>
              <div class="send-email">
                <template v-for="sendType in sendEmailTypes">
                  <b-field horizontal :key="`${sendType.label}_switch`">
                    <template #label>
                      <label class="mr-1">{{ sendType.label }}</label>
                      <b-tooltip
                        :label="sendType.tooltip"
                        multilined
                        type="is-dark"
                      >
                        <i class="icon-help-circle-outline text-gray-500" />
                      </b-tooltip>
                    </template>
                    <b-switch
                      v-model="orderInfo[sendType.switch]"
                      :disabled="viewMode"
                      class="mx-2"
                      size="is-small"
                      />
                  </b-field>
                  <b-field
                    v-if="orderInfo[sendType.switch]"
                    :key="`${sendType.label}_email`"
                    :message="
                      hasEmailFills[sendType.email]
                        ? v$.orderInfo[sendType.email].$errors[0].$message
                        : ''
                    "
                    :type="{ 'is-danger': hasEmailFills[sendType.email] }"
                    horizontal
                  >
                    <template #label>
                      <span class="required">Email</span>
                    </template>
                    <b-taginput
                      v-model="orderInfo[sendType.email]"
                      :before-adding="beforeAddEmail"
                      :disabled="viewMode"
                      :ref="`${sendType.email}_taginput`"
                      @input="v$.orderInfo[sendType.email] && v$.orderInfo[sendType.email].$touch()"
                    >
                      <template #selected="props">
                        <b-tag
                          v-for="(tag, index) in props.tags"
                          :closable="!viewMode"
                          :key="`email-tag-${tag}-${index}`"
                          @close="closeTags(sendType, index, $event)"
                          rounded
                          type="is-primary-light"
                        >
                          {{ tag }}
                        </b-tag>
                      </template>
                    </b-taginput>
                  </b-field>
                </template>
              </div>
            </div>
          </div>
        </div>
        <div class="column">
          <div class="detail-card order-info">
            <h3 class="font-medium mb-4">
              訂購資訊
            </h3>
            <div>
              <b-field
                horizontal
                :type="{
                  'is-danger': v$.orderInfo.name.$dirty && v$.orderInfo.name.$invalid
                }"
                :message="
                  v$.orderInfo.name.$dirty &&
                  v$.orderInfo.name.$silentErrors.length
                    ? v$.orderInfo.name.$silentErrors[0].$message
                    : ''
                ">
                <template #label>
                  <span class="required">訂購名稱</span>
                </template>
                <b-input
                  :disabled="viewMode"
                  name="name"
                  v-model="orderInfo.name"
                  @input="v$.orderInfo.name.$touch" />
              </b-field>
              <b-field horizontal>
                <template #label>
                  <label class="mr-1">訂購標籤</label>
                  <b-tooltip
                    label="分類標籤是方便於訂購發布後，統計報表篩選使用，僅會顯示在管理平台。"
                    multilined
                    type="is-dark"
                  >
                    <i class="icon-help-circle-outline text-gray-500" />
                  </b-tooltip>
                </template>
                <b-taginput
                  ref="createTag"
                  :disabled="viewMode"
                  name="tags"
                  v-model="orderInfo.tags"
                  placeholder="建立標籤"
                >
                  <template #selected="props">
                    <b-tag
                      v-for="(tag, index) in props.tags"
                      :key="`tag-${tag}-${index}`"
                      :closable="!viewMode"
                      rounded
                      type="is-primary-light"
                      @close="$refs.createTag.removeTag(index, $event)"
                    >
                      {{ tag }}
                    </b-tag>
                  </template>
                </b-taginput>
              </b-field>
              <b-field horizontal label="訂購備註">
                <b-input
                  v-model="orderInfo.note"
                  :disabled="viewMode"
                  placeholder="備註將會顯示在前台訂購資訊"
                  maxlength="100"
                  has-counter
                />
              </b-field>
            </div>
          </div>
          <schedule-period-setting
            v-if="schedule"
            v-model="schedule"
            :createMode="createMode"
            :viewMode="hasFirstGroup || viewMode"
            @init="onPeriodInit"
            @input="onPeriodUpdate"
            @error="periodError = $event" />
        </div>
      </div>
      <div class="flex gap-3 items-center mt-6 pl-4">
        <span class="font-medium text-xl">訂購列表</span>
        <w-button
          type="icon"
          @click="listCollapsed = !listCollapsed"
        >
          <i
            :class="{ collapsed: listCollapsed }"
            class="icon-drop-down text-2xl text-gray-500"
          />
        </w-button>
      </div>
      <div v-show="!listCollapsed" class="columns order-detail__content">
        <schedule-order-list
          v-model="exclude"
          :pending="scheduleOrderList"
          :created="groupBuyings"
          :viewMode="viewMode"
          @pending-list="pendingList = $event" />
      </div>
    </div>
    <div
      v-if="!inDialog"
      class="flex justify-end wrapper__action-bar"
      :class="{ 'ml-0 sm:ml-0': isSidebarCollapsed }"
    >
      <div v-if="!pendingList?.length" class="flex items-center w-fit">
        <span class="icon-alert-circle ml-3.5 text-red-500 text-lg" />
        <span class="ml-2.5 mr-6 text-red-500 text-sm">
          目前指定的排程無法建立任何訂購，請確認領取週期範圍、領取時段及開放訂購時間是否正確。
        </span>
      </div>
      <w-button
        v-if="!viewMode"
        :disabled="loading"
        class="mr-8"
        type="text"
        @click="goBack"
      >
        取消
      </w-button>
      <w-button
        v-if="createMode"
        :disabled="loading"
        @click="onSubmit()"
      >
        建立訂購排程
      </w-button>
      <w-button
        v-else-if="viewMode"
        @click="goBack"
      >
        返回
      </w-button>
      <w-button
        v-else
        :disabled="loading"
        @click="onSubmit()"
      >
        確認
      </w-button>
    </div>
    <store-selector-modal
      v-if="openStoreSelectorModal"
      v-model="openStoreSelectorModal"
      :menu="menu"
      @close="openStoreSelectorModal = false"
      @menu="setMenu"
    />
  </section>
</template>

<script>
import ScheduleSetting from './ScheduleSetting';

export default ScheduleSetting;
</script>

<style lang="scss" scoped>
@import './ScheduleSetting.scss';
</style>
